import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';
import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components/ui/follow-up-button';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { OpenChatButton } from '../../common/ui/open-chat/open-chat-button';
import { useAIAnswerContext } from '../ai-answer-context';
import { messages as definitionAnswerCardMessages } from '../definition-answer-card/messages';
import { onExpandButtonClicked } from '../on-expand-button-clicked';
import { onFollowUpSuggestionClick } from '../on-follow-up-suggestion-click';

import { messages } from './messages';

const styles = cssMap({
	followUpWrapperStyles: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.100'),
	},
	followUpWrapperUpdateStyles: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	dividerStyles: {
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		borderTopColor: token('color.border'),
	},
});

export const ReadingAidsFollowUps = (): React.JSX.Element | null => {
	const { isAnswered, commonAttributes, onClose, readingAidsOptions } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const { query, answerString, followUps } = commonAttributes;
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	const [showAllFollowUps, setShowAllFollowups] = useState(false);
	const canExpand = followUps.length > 1;
	const visibleFollowUps = showAllFollowUps ? followUps : followUps.slice(0, 1);

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	const onFollowupsExpand = useCallback(() => {
		fireAnalyticsEvent(
			onExpandButtonClicked({ ...commonAttributes, expandButtonSource: 'follow-ups' }),
		);
		setShowAllFollowups(!showAllFollowUps);
	}, [fireAnalyticsEvent, commonAttributes, showAllFollowUps]);

	const onFollowUpClick = useCallback(
		(followUp: string) => {
			if (readingAidsOptions?.onFollowUpCallback) {
				readingAidsOptions.onFollowUpCallback(followUp);
				return;
			}

			fireAnalyticsEvent(
				onFollowUpSuggestionClick({ ...commonAttributes, followUpString: followUp }),
			);
			publish({
				type: 'chat-new',
				source: 'reading-aids',
				data: {
					name: followUp.substring(0, 30),
					dialogues: [
						{
							human_message: {
								content: formatMessage(messages.reading_aids_follow_ups_user_initial_query, {
									// query only contains the word that user asked
									// we want to add the word "define" before it so it makes sense
									// when this is moved to the chat
									term: query,
								}),
							},
							agent_message: { content: answerString },
						},
					],
					prompt: followUp,
				},
			});

			onClose?.();
		},
		[
			query,
			answerString,
			publish,
			formatMessage,
			onClose,
			readingAidsOptions,
			commonAttributes,
			fireAnalyticsEvent,
		],
	);

	if (!followUps?.length) {
		return null;
	}

	return (
		<Box
			xcss={cx(
				styles.followUpWrapperStyles,
				serveDefinitionUXUpdates && styles.followUpWrapperUpdateStyles,
			)}
		>
			<Stack space="space.100">
				{serveDefinitionUXUpdates && <Box xcss={styles.dividerStyles} />}
				{visibleFollowUps.map((followUp, index) => (
					<Inline
						space="space.050"
						alignBlock="center"
						spread={serveDefinitionUXUpdates ? 'space-between' : undefined}
					>
						{serveDefinitionUXUpdates ? (
							<Button
								key={followUp}
								iconBefore={RovoChatIcon}
								spacing="compact"
								onClick={() => onFollowUpClick(followUp)}
								testId="definition-follow-up-button"
							>
								<Text maxLines={1} color="color.text.subtle" weight="medium">
									{followUp}
								</Text>
							</Button>
						) : (
							<FollowUpButton
								key={followUp}
								followUpIndex={index}
								followUpText={followUp}
								followUpType="UNAVAILABLE"
								experienceId="reading-aids"
								onClick={() => onFollowUpClick(followUp)}
							/>
						)}
						{index === 0 && canExpand && (
							<IconButton
								onClick={onFollowupsExpand}
								icon={(iconProps) =>
									showAllFollowUps ? (
										<ChevronDownIcon
											{...iconProps}
											color="currentColor"
											testId="collapse-follow-ups-icon"
											size="small"
										/>
									) : (
										<ChevronRightIcon
											{...iconProps}
											color="currentColor"
											testId="expand-follow-ups-icon"
											size="small"
										/>
									)
								}
								appearance="subtle"
								label={
									showAllFollowUps
										? fg('a11y-fix-icon-button-label-no-role')
											? 'Collapse follow ups'
											: 'Collapse follow ups button'
										: fg('a11y-fix-icon-button-label-no-role')
											? 'Expand follow ups'
											: 'Expand follow ups button'
								}
							/>
						)}
					</Inline>
				))}
				{isAnswered && serveDefinitionUXUpdates && (
					<OpenChatButton
						prompt={formatMessage(
							definitionAnswerCardMessages.definition_open_chat_user_initial_query,
							{
								// query only contains the highlighted word
								// we want to prefix the word "define" before
								// when a user's context is moved to chat
								term: query,
							},
						)}
						answerString={answerString}
						query={query}
						source={'reading-aids'}
					/>
				)}
			</Stack>
		</Box>
	);
};
